import type { ReactNode } from 'react';
import { Check } from 'lucide-react';

export type TwoStepMode = 'care-seeker' | 'fp';
export type TwoStepId = 'intake' | 'results';

type Step = {
  id: TwoStepId;
  label: string;
  description: string;
};

const stepsByMode: Record<TwoStepMode, Step[]> = {
  'care-seeker': [
    { id: 'intake', label: 'Your Story', description: 'Tell us about your situation' },
    { id: 'results', label: 'Your Care Guide', description: 'Matched specialists & resources' },
  ],
  fp: [
    { id: 'intake', label: 'Your Story', description: 'Tell us about your client' },
    { id: 'results', label: 'Your Matches', description: 'Recommended specialists & resources' },
  ],
};

type TwoStepProgressProps = {
  currentStep: TwoStepId;
  mode?: TwoStepMode;
  /** Optional action rendered at the left edge of the header, separate from the steps. */
  leftAction?: ReactNode;
};

export const TwoStepProgress = ({
  currentStep,
  mode = 'care-seeker',
  leftAction,
}: TwoStepProgressProps) => {
  const steps = stepsByMode[mode];
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStep),
  );

  return (
    <div className="sticky top-[var(--app-topbar-h,4rem)] z-40 border-b border-border-subtle bg-background px-4 py-4 shadow-sm">
      <div className="grid grid-cols-[1fr_minmax(0,42rem)_1fr] items-center gap-2">
        <div className="flex justify-start">{leftAction}</div>
        <div className="flex w-full items-center">
          {steps.map((step, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isLast = index === steps.length - 1;

            return (
              <div key={step.id} className="flex flex-1 items-center">
                <div
                  className="flex flex-col items-center gap-1.5 text-center"
                  {...(isCurrent ? { 'data-testid': 'two-step-current' } : {})}
                >
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                      isCompleted || isCurrent
                        ? 'bg-sage text-white'
                        : 'bg-card-subtle text-text-tertiary'
                    } ${isCurrent ? 'ring-4 ring-sage/20' : ''}`}
                  >
                    {isCompleted ? <Check className="h-4 w-4" aria-hidden /> : <span>{index + 1}</span>}
                  </span>
                  <span
                    className={`font-termina text-sm font-medium ${
                      isCurrent || isCompleted ? 'text-text-primary' : 'text-text-tertiary'
                    }`}
                  >
                    {step.label}
                  </span>
                  <span className="hidden text-xs text-text-secondary sm:block">
                    {step.description}
                  </span>
                </div>

                {!isLast && (
                  <div className="mx-3 mt-[-1.25rem] h-0.5 flex-1 rounded-full bg-border-subtle">
                    <div
                      className={`h-full rounded-full ${isCompleted ? 'bg-sage' : 'bg-transparent'}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div aria-hidden />
      </div>
    </div>
  );
};
